import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { commonMessages } from 'src/features/auth/utils';
import { MEMBERSHIP_USER_ACCESS_LEVEL } from 'src/features/membership/types';
import CellMembershipInvitationActions from 'src/features/membershipCloudUsers/components/cells/CellMembershipInvitationActions';
import CreateMembershipCloudUserFormModal from 'src/features/membershipCloudUsers/components/forms/CreateMembershipCloudUserFormModal';
import { MEMBERSHIP_USER_ACCESS_LEVEL_BADGES } from 'src/features/membershipCloudUsers/utils/constants';
import { membershipUsersMessages } from 'src/features/membershipCloudUsers/utils/messages';
import { useListInvitations } from 'src/features/users/api/useListInvitations';
import { CellExpiration } from 'src/features/users/components/tables/CellExpiration';
import { userMessages } from 'src/features/users/utils/messages';
import { Invitation } from 'src/features/users/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import {
    DataTable,
    DataTablePagination,
    DataTableColumnHeader,
    DataTableControls,
    CellBadge,
} from 'src/registry/components/table';
import { CellTitleSubtitle } from 'src/registry/components/table/cells/CellTitleSubtitle';
import useFilters from 'src/registry/hooks/useFilters';

type MembershipInvitationTableProps = {
    membershipId: string;
    isClient?: boolean;
    accessLevel?: MEMBERSHIP_USER_ACCESS_LEVEL;
};

const MembershipInvitationTable = ({ membershipId, isClient, accessLevel }: MembershipInvitationTableProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const columnHelper = createColumnHelper<Invitation>();

    const permissionManageMemberships = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);
    const showCreateCloudUser =
        (permissionManageMemberships && membershipId && !isClient) ||
        (isClient && accessLevel && accessLevel === MEMBERSHIP_USER_ACCESS_LEVEL.ADMIN);

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });

    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'created_at', desc: true }],
    });

    const { data, isLoading, isError } = useListInvitations({
        parkingId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        sp_membership_id: membershipId,
        config: { enabled: !!parkingId },
    });

    const columns = [
        columnHelper.accessor('email', {
            id: 'email',
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(userMessages.email),
            },
        }),
        columnHelper.accessor('inviter.email', {
            id: 'inviter__email',
            header: DataTableColumnHeader,
            cell: (cellProps) =>
                CellTitleSubtitle({
                    title: cellProps.row.original.inviter.name,
                    subtitle: cellProps.row.original.inviter.email,
                    ...cellProps,
                }),
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userMessages.invitedBy),
            },
        }),
        columnHelper.accessor('sp_membership_access_level', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(membershipUsersMessages.accessLevel),
                badgeOptions: MEMBERSHIP_USER_ACCESS_LEVEL_BADGES,
            },
        }),
        columnHelper.accessor('expires_at', {
            header: DataTableColumnHeader,
            cell: CellExpiration,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(userMessages.expires),
            },
        }),
        ...(showCreateCloudUser
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: (ctx) => {
                          return <CellMembershipInvitationActions {...ctx} row={ctx.row} membershipId={membershipId} />;
                      },
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<Invitation, any>
            columns={columns}
            data={data}
            pagination={pagination}
            sorting={sorting}
            isLoading={isLoading}
            isError={isError}
            filters={filters}
            search={search}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(userMessages.invitations),
                entryName: formatMessage(userMessages.invitation),
                entryNames: formatMessage(userMessages.invitations),
                placeholder: formatMessage(commonMessages.email),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(showCreateCloudUser && {
                    newEntry: () => (
                        <CreateMembershipCloudUserFormModal membershipId={membershipId} isInvitation={true} />
                    ),
                }),
            }}
        />
    );
};

export { MembershipInvitationTable };
